import type {
  IncidentSeverity,
  IncidentSource,
  IncidentStatus,
} from '@travel-planner/shared/schemas';
import { joinCss } from '@/shared/lib/css-utils';
import { Badge } from '@/shared/ui';
import css from './incident-badges.module.scss';

const humanize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');

// Status → shared Badge variant (matches the app's semantic colours):
// open = amber (needs attention), in_progress = blue (being worked),
// resolved = green (done), closed = grey (archived), cancelled = red (voided).
const STATUS_VARIANT: Record<
  IncidentStatus,
  'pending' | 'booked' | 'active' | 'inactive' | 'error'
> = {
  open: 'pending',
  in_progress: 'booked',
  resolved: 'active',
  closed: 'inactive',
  cancelled: 'error',
};

export const IncidentStatusBadge = ({ status }: { status: IncidentStatus }) => (
  <Badge
    className={joinCss(css.badge, css.statusBadge)}
    dot
    variant={STATUS_VARIANT[status]}
  >
    {humanize(status)}
  </Badge>
);

// Severity → shared Badge variant: critical = red, high/medium/low reuse the
// app's priority palette.
const SEVERITY_VARIANT: Record<
  IncidentSeverity,
  'error' | 'high' | 'medium' | 'low'
> = {
  critical: 'error',
  high: 'high',
  medium: 'medium',
  low: 'low',
};

export const IncidentSeverityBadge = ({
  severity,
}: {
  severity: IncidentSeverity;
}) => (
  <Badge className={css.badge} variant={SEVERITY_VARIANT[severity]}>
    {humanize(severity)}
  </Badge>
);

// Where the row came from — rendered ONLY for rows nobody filed through the
// product, so an ordinary report stays unadorned. A migrated REGISTO record and
// a report a consultant typed must not look alike: the first has no reporter who
// remembers it, and its blank fields are gaps in a spreadsheet rather than
// choices someone made.
const SOURCE_LABEL: Partial<Record<IncidentSource, string>> = {
  import: 'Imported',
  red_flag: 'Red flag',
};

export const IncidentSourceBadge = ({ source }: { source: IncidentSource }) => {
  const label = SOURCE_LABEL[source];
  if (!label) return null;
  return (
    <Badge className={joinCss(css.badge, css.sourceBadge)} variant="inactive">
      {label}
    </Badge>
  );
};
